import { TApiResponse } from "../../types/api";

export enum FileType {
  ONBOARDING_ANSWER = "onboarding_answer",
  CHAT_ATTACHMENT = "chat_attachment",
  PROFILE_DOCUMENT = "profile_document",
  COMPANY_DOCUMENT = "company_document",
  TODO_TASK_RESPONSE = "todo_task_response",
}

export enum FileStatus {
  ACTIVE = "active",
  ARCHIVED = "archived",
  SCHEDULED_FOR_DELETION = "scheduled_for_deletion",
  DELETED = "deleted",
}

export enum StorageProvider {
  LOCAL = "local",
  S3 = "s3",
  GCS = "gcs",
  MINIO = "minio",
}

export type TFileMetadata = {
  ragflow_uploaded?: boolean;
  ragflow_uploaded_at?: string;
  ragflow_document_id?: string;
  ragflow_dataset_id?: string;
  ragflow_dataset_name?: string;
  [key: string]: unknown;
};

export type TFileResponse = {
  id: string;
  storage_provider: StorageProvider;
  storage_path: string;
  original_filename: string;
  file_size: number;
  content_type: string;
  file_hash: string;
  uploaded_by: string;
  company_id: string | null;
  file_type: FileType;
  is_public: boolean;
  status: FileStatus;
  file_metadata: TFileMetadata | null;
  created_at: string;
  updated_at: string;
};

export type TFileListResponse = {
  files: TFileResponse[];
  total: number;
  page: number;
  size: number;
};

export type TFileListApiResponse = TApiResponse<TFileListResponse>;
export type TFileApiResponse = TApiResponse<TFileResponse>;
export type TDeleteFileApiResponse = TApiResponse<{ deleted: boolean }>;

export type TUploadFilePayload = {
  file: File;
  fileType: FileType;
  companyId?: string;
  contextId?: string;
  isPublic?: boolean;
  consentRequired?: boolean;
  retentionDays?: number;
};

export type TSignedUrlResponse = {
  url: string;
  expires_in: number;
};

/**
 * File types that make up a company's knowledge base.
 *
 * The test for membership is whether the file is indexed into the company's RAGFlow dataset —
 * that dataset *is* the knowledge base, so anything in it should appear on the only screen
 * that shows it. Onboarding attachments, chat attachments and to-do task responses all land
 * there alongside documents added from the knowledge card, and filtering on COMPANY_DOCUMENT
 * alone hid a founder's pitch deck and every document they submitted against a task.
 *
 * Where a file came from still matters to the person reading the list, so the knowledge base
 * groups by origin rather than dropping types — see KNOWLEDGE_CATEGORIES.
 */
export const KNOWLEDGE_BASE_FILE_TYPES = [
  FileType.COMPANY_DOCUMENT,
  FileType.ONBOARDING_ANSWER,
  FileType.CHAT_ATTACHMENT,
  FileType.TODO_TASK_RESPONSE,
] as const;

/** Comma-separated form for the `file_type` query parameter. */
export const KNOWLEDGE_BASE_FILE_TYPE_PARAM = KNOWLEDGE_BASE_FILE_TYPES.join(",");
